import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AttendanceService } from "@/services/Attendance/AttendanceService";
import { ThrowError } from "@/api/callApi";
import { showToastNotification } from "@/utility/helper";
import type {
  ISalaryPreviewData,
  ICreateUserMonthlySalaryPayload,
} from "@/interface/attendance.interface";
import { RoutesLink } from "@/utility/RouteLinks";
import type { ISalaryPreviewDraftResult } from "./useSalaryPreviewDraft";

interface UseSalaryPreviewActionsParams {
  previewData: ISalaryPreviewData | null;
  setPreviewData: React.Dispatch<
    React.SetStateAction<ISalaryPreviewData | null>
  >;
  locationState: {
    siteId?: string;
  } | null;
  userSiteId?: string;
  startDate: string;
  endDate: string;
  draft: Pick<
    ISalaryPreviewDraftResult,
    | "draftStorageKey"
    | "saveDraftToStorage"
    | "setDraftEdits"
    | "setIsDraftActive"
    | "setDraftTimestamp"
    | "applyDraftAndRecalculateUsers"
  >;
}

export interface ISalaryPreviewActionsResult {
  isSubmitting: boolean;
  handleSalaryChange: (
    userId: string,
    headId: string,
    amount: number
  ) => void;
  handleSaveUserSalary: (
    userId: string,
    userSalaryList: { branchSalaryHeadId: string; amount: number }[]
  ) => Promise<void>;
  handleCreateSalary: () => Promise<void>;
}

export function useSalaryPreviewActions({
  previewData,
  setPreviewData,
  locationState,
  userSiteId,
  startDate,
  endDate,
  draft,
}: UseSalaryPreviewActionsParams): ISalaryPreviewActionsResult {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Destructure stable refs to avoid object identity triggering re-renders
  const {
    setDraftEdits,
    saveDraftToStorage,
    applyDraftAndRecalculateUsers,
    draftStorageKey,
    setIsDraftActive,
    setDraftTimestamp,
  } = draft;

  /**
   * Called when a single salary head input changes in SalaryConfigTable.
   * Saves to draft and updates previewData in real-time (so Tab 1 totals update).
   */
  const handleSalaryChange = useCallback(
    (userId: string, headId: string, amount: number) => {
      setDraftEdits((prev) => {
        const updatedUser = { ...(prev[userId] || {}), [headId]: amount };
        const newEdits = { ...prev, [userId]: updatedUser };
        saveDraftToStorage(newEdits);

        setPreviewData((prevData) => {
          if (!prevData?.users) return prevData;
          return {
            ...prevData,
            users: applyDraftAndRecalculateUsers(prevData.users, newEdits),
          };
        });

        return newEdits;
      });
    },
    [setDraftEdits, saveDraftToStorage, applyDraftAndRecalculateUsers, setPreviewData]
  );

  /**
   * Called when Save button is clicked for a row in SalaryConfigTable.
   * Merges all row values into draft and recalculates.
   */
  const handleSaveUserSalary = useCallback(
    async (
      userId: string,
      userSalaryList: { branchSalaryHeadId: string; amount: number }[]
    ) => {
      const userUpdates: Record<string, number> = {};
      userSalaryList.forEach((item) => {
        if (item.branchSalaryHeadId) {
          userUpdates[item.branchSalaryHeadId] = item.amount;
        }
      });

      setDraftEdits((prev) => {
        const updatedUser = { ...(prev[userId] || {}), ...userUpdates };
        const newEdits = { ...prev, [userId]: updatedUser };
        saveDraftToStorage(newEdits);

        setPreviewData((prevData) => {
          if (!prevData?.users) return prevData;
          return {
            ...prevData,
            users: applyDraftAndRecalculateUsers(prevData.users, newEdits),
          };
        });

        return newEdits;
      });

      showToastNotification("success", "Draft salary updated locally.");
    },
    [setDraftEdits, saveDraftToStorage, applyDraftAndRecalculateUsers, setPreviewData]
  );

  /**
   * Submits the final salary payload to the backend.
   * Clears local draft on success and navigates back.
   */
  const handleCreateSalary = useCallback(async () => {
    const currentUsers = previewData?.users || [];
    if (currentUsers.length === 0) {
      showToastNotification(
        "warning",
        "No employee data available to create salary."
      );
      return;
    }

    const userIds = currentUsers
      .map((u) => u.user?.id)
      .filter(Boolean) as string[];

    if (userIds.length === 0) {
      showToastNotification(
        "warning",
        "Invalid employee IDs for salary creation."
      );
      return;
    }

    const usersPayload = currentUsers
      .map((u) => ({
        userId: u.user?.id || "",
        userSalary: (u.userSalary || [])
          ?.map((sal) => ({
            branchSalaryHeadId: sal.branchSalaryHeadId || sal.id || "",
            amount: Number(sal.amount) || 0,
          }))
          .filter((item) => item.branchSalaryHeadId !== ""),
      }))
      .filter((item) => item.userId !== "");

    const payload: ICreateUserMonthlySalaryPayload = {
      userIds,
      siteId: locationState?.siteId || userSiteId || "",
      startDate,
      endDate,
      users: usersPayload,
    };

    setIsSubmitting(true);
    try {
      const response =
        await AttendanceService.createUserMonthlySalary(payload);
      if (
        response?.status ||
        response?.statuscode === 200 ||
        response?.statuscode === 201
      ) {
        showToastNotification(
          "success",
          response?.message || "Monthly salary created successfully!"
        );

        // Clear draft after successful creation
        try {
          localStorage.removeItem(draftStorageKey);
        } catch (e) {
          console.error("Failed to clear draft after salary creation:", e);
        }
        setIsDraftActive(false);
        setDraftEdits({});
        setDraftTimestamp(null);

        setTimeout(() => {
          navigate(RoutesLink.monthlyTeamAttendance);
        }, 1000);
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to create monthly salary."
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    previewData?.users,
    locationState?.siteId,
    userSiteId,
    startDate,
    endDate,
    draftStorageKey,
    setIsDraftActive,
    setDraftEdits,
    setDraftTimestamp,
    navigate,
  ]);

  return { isSubmitting, handleSalaryChange, handleSaveUserSalary, handleCreateSalary };
}
